import { notification, platform } from "@todesktop/client-core"
import { readFile } from "@todesktop/client-fs"
import { useState } from "react"

// Use existing type declarations
declare global {
  interface Window {
    // @ts-ignore TODO: Fix conflicting todesktop type declarations
    todesktop?: any
    electronAPI?: {
      readClaudeConfig: () => Promise<{ success: boolean; data?: any; error?: string }>
      platform: string
      isElectron: boolean
      versions: {
        node: string
        chrome: string
        electron: string
      }
    }
  }
}

interface ClaudeProject {
  path: string
  mcpServers?: Record<string, any>
}

export default function ElectronNativeScreen() {
  const [notificationStatus, setNotificationStatus] = useState<string>("")
  const [customSound, setCustomSound] = useState<string>("")
  const [notificationTitle, setNotificationTitle] = useState<string>("Hello from AIX!")
  const [notificationBody, setNotificationBody] = useState<string>("This is a test notification from your desktop app.")

  // File system state
  const [fileSystemStatus, setFileSystemStatus] = useState<string>("")
  const [claudeProjects, setClaudeProjects] = useState<ClaudeProject[]>([])
  const [selectedProject, setSelectedProject] = useState<ClaudeProject | null>(null)
  const [claudeConfig, setClaudeConfig] = useState<any>(null)

  const isDesktopApp = () => {
    try {
      // Use ToDesktop client-core for proper platform detection
      return platform.todesktop.isDesktopApp()
    } catch (error) {
      // Fallback: check for Electron environment
      return (
        typeof window !== "undefined" &&
        // Check for our electronAPI (from preload script)
        (window.electronAPI?.isElectron ||
          // Check for Electron user agent
          navigator.userAgent.includes("Electron") ||
          // Check for electron in process versions
          (window as any).process?.versions?.electron ||
          // Generic electron check
          (window as any).require)
      )
    }
  }

  const isToDesktopApp = () => {
    try {
      return platform.todesktop.isDesktopApp()
    } catch (error) {
      return false
    }
  }

  const isLocalElectron = () => {
    return isDesktopApp() && !isToDesktopApp()
  }

  const hasNativeFileAccess = () => {
    return typeof window !== "undefined" && window.electronAPI?.readClaudeConfig
  }

  const checkNotificationSupport = () => {
    if (!("Notification" in window)) {
      return "Notifications are not supported in this browser."
    }

    if (isDesktopApp()) {
      return "Running in ToDesktop - notifications are automatically supported!"
    }

    return `Browser support: ${Notification.permission}`
  }

  const sendBasicNotification = async () => {
    try {
      setNotificationStatus("Sending notification...")

      if (isDesktopApp()) {
        // Use ToDesktop notification API when running in desktop app
        try {
          await notification.create({
            title: notificationTitle,
            body: notificationBody,
            icon: "/icon.png",
          })
          setNotificationStatus("✅ ToDesktop notification sent!")
          return
        } catch (todesktopError) {
          console.warn("ToDesktop notification failed, falling back to HTML5:", todesktopError)
        }
      }

      // Fallback to HTML5 Notification for web or if ToDesktop fails
      if (!("Notification" in window)) {
        setNotificationStatus("❌ Notifications not supported")
        return
      }

      // Request permission if needed (only for web)
      if (!isDesktopApp() && Notification.permission !== "granted") {
        const permission = await Notification.requestPermission()
        if (permission !== "granted") {
          setNotificationStatus("❌ Permission denied")
          return
        }
      }

      const htmlNotification = new Notification(notificationTitle, {
        body: notificationBody,
        icon: "/icon.png", // Using the app icon
        badge: "/icon.png",
        // @ts-ignore TODO: Fix Notification tag property
        tag: "aix-test-notification",
        requireInteraction: false,
      })

      htmlNotification.onclick = () => {
        console.log("Notification clicked!")
        window.focus()
        htmlNotification.close()
      }

      setNotificationStatus("✅ HTML5 notification sent!")

      // Auto-close after 4 seconds
      setTimeout(() => {
        htmlNotification.close()
      }, 4000)
    } catch (error) {
      console.error("Notification error:", error)
      setNotificationStatus(`❌ Error: ${error instanceof Error ? error.message : "Unknown error"}`)
    }
  }

  const sendNotificationWithSound = async () => {
    try {
      setNotificationStatus("Sending notification with sound...")

      if (!isDesktopApp()) {
        setNotificationStatus("❌ Custom sounds only work in desktop app")
        return
      }

      // Use ToDesktop notification API for better control
      try {
        const notificationOptions: any = {
          title: notificationTitle,
          body: notificationBody,
          icon: "/icon.png",
          tag: "aix-sound-notification",
        }

        // Add custom sound if specified (ToDesktop feature)
        if (customSound) {
          notificationOptions.sound = customSound
        }

        await notification.create(notificationOptions)
        setNotificationStatus("✅ ToDesktop notification with sound sent!")
      } catch (todesktopError) {
        console.warn("ToDesktop notification failed, falling back to HTML5:", todesktopError)

        // Fallback to HTML5 Notification with sound
        const notificationOptions: NotificationOptions & { sound?: string } = {
          body: notificationBody,
          icon: "/icon.png",
          badge: "/icon.png",
          // @ts-ignore TODO: Fix Notification tag property
          tag: "aix-sound-notification",
          requireInteraction: false,
        }

        // Add custom sound if specified (ToDesktop feature)
        if (customSound) {
          ;(notificationOptions as any).sound = customSound
        }

        const htmlNotification = new Notification(notificationTitle, notificationOptions)

        htmlNotification.onclick = () => {
          console.log("Sound notification clicked!")
          window.focus()
          htmlNotification.close()
        }

        setNotificationStatus("✅ HTML5 notification with sound sent!")

        // Auto-close after 5 seconds
        setTimeout(() => {
          htmlNotification.close()
        }, 5000)
      }
    } catch (error) {
      console.error("Sound notification error:", error)
      setNotificationStatus(`❌ Error: ${error instanceof Error ? error.message : "Unknown error"}`)
    }
  }

  const sendToDesktopNotification = async () => {
    try {
      setNotificationStatus("Sending ToDesktop notification...")

      if (!isDesktopApp()) {
        setNotificationStatus("❌ ToDesktop API only works in desktop app")
        return
      }

      await notification.create({
        title: notificationTitle,
        body: notificationBody,
        icon: "/icon.png",
      })

      setNotificationStatus("✅ ToDesktop notification sent successfully!")
    } catch (error) {
      console.error("ToDesktop notification error:", error)
      setNotificationStatus(`❌ ToDesktop API Error: ${error instanceof Error ? error.message : "Unknown error"}`)
    }
  }

  const sendTimedNotification = () => {
    setNotificationStatus("⏱️ Notification scheduled for 3 seconds...")

    setTimeout(() => {
      void sendBasicNotification()
    }, 3000)
  }

  // File system functions
  const readClaudeConfig = async () => {
    try {
      setFileSystemStatus("📖 Reading Claude configuration...")

      if (!isDesktopApp()) {
        setFileSystemStatus("❌ File system access only works in desktop app")
        return
      }

      // Try native Electron file access first (local development)
      if (hasNativeFileAccess()) {
        setFileSystemStatus("📖 Using native Electron file system...")

        const result = await window.electronAPI.readClaudeConfig()

        if (result.success && result.data) {
          const config = result.data
          setClaudeConfig(config)

          // Extract projects from the config
          if (config.projects) {
            const projectPaths = Object.keys(config.projects)
            const projects: ClaudeProject[] = projectPaths.map((path) => ({
              path,
              mcpServers: config.projects[path]?.mcpServers,
            }))

            setClaudeProjects(projects)
            setFileSystemStatus(`✅ Found ${projects.length} Claude projects (Native Electron)`)
          } else {
            setClaudeProjects([])
            setFileSystemStatus("✅ Configuration loaded but no projects found (Native Electron)")
          }
          return
        } else {
          setFileSystemStatus(`❌ Native Electron error: ${result.error}`)
          return
        }
      }

      // For ToDesktop builds, use the file system API
      if (isToDesktopApp()) {
        try {
          // Read the .claude.json file from the user's home directory
          const fileContents = await readFile("appData", "../.claude.json")

          const config = JSON.parse(fileContents)
          setClaudeConfig(config)

          // Extract projects from the config
          if (config.projects) {
            const projectPaths = Object.keys(config.projects)
            const projects: ClaudeProject[] = projectPaths.map((path) => ({
              path,
              mcpServers: config.projects[path]?.mcpServers,
            }))

            setClaudeProjects(projects)
            setFileSystemStatus(`✅ Found ${projects.length} Claude projects (ToDesktop)`)
          } else {
            setClaudeProjects([])
            setFileSystemStatus("✅ Configuration loaded but no projects found")
          }
          return
        } catch (error) {
          console.error("Error reading Claude config:", error)

          // Try alternative path if first attempt fails
          try {
            setFileSystemStatus("📖 Trying alternative path...")
            const fileContents = await readFile("userData", "../.claude.json")
            const config = JSON.parse(fileContents)
            setClaudeConfig(config)

            if (config.projects) {
              const projectPaths = Object.keys(config.projects)
              const projects: ClaudeProject[] = projectPaths.map((path) => ({
                path,
                mcpServers: config.projects[path]?.mcpServers,
              }))

              setClaudeProjects(projects)
              setFileSystemStatus(`✅ Found ${projects.length} Claude projects (ToDesktop - alternative path)`)
            }
            return
          } catch (altError) {
            setFileSystemStatus(
              `❌ ToDesktop file system failed. Enable File System plugin in ToDesktop Builder and ensure readFile is enabled.`,
            )
          }
        }
      }

      // Fallback message
      setFileSystemStatus(
        "❌ No file system access available. Try running with ToDesktop or check Electron configuration.",
      )
    } catch (error) {
      console.error("Error reading Claude config:", error)
      setFileSystemStatus(`❌ Error: ${error instanceof Error ? error.message : "Failed to read config"}`)
    }
  }

  const selectProject = (project: ClaudeProject) => {
    setSelectedProject(project)
    setFileSystemStatus(`📁 Selected project: ${project.path}`)
  }

  const clearSelection = () => {
    setSelectedProject(null)
    setFileSystemStatus("")
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-background  shadow-lg rounded-lg">
        <div className="p-6 border-b border-border dark:border-gray-700">
          <h1 className="text-2xl font-bold  dark:text-white">⚡ Electron Native Features</h1>
          <p className="mt-2  dark:text-gray-400">Test native notifications and other desktop-specific features</p>
        </div>

        <div className="p-6 space-y-6">
          {/* Environment Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">🔍 Environment Information</h2>
            <div className="space-y-2 text-sm">
              <p className="text-blue-800 dark:text-blue-200">
                <strong>Platform:</strong>{" "}
                {isToDesktopApp() ? "🖥️ ToDesktop App" : isLocalElectron() ? "⚡ Local Electron" : "🌐 Web Browser"}
              </p>
              <p className="text-blue-800 dark:text-blue-200">
                <strong>Notification Support:</strong> {checkNotificationSupport()}
              </p>
              <p className="text-blue-800 dark:text-blue-200">
                <strong>File System:</strong>{" "}
                {isToDesktopApp()
                  ? "✅ ToDesktop FS Available"
                  : hasNativeFileAccess()
                    ? "✅ Native Electron FS Available"
                    : isLocalElectron()
                      ? "⚠️ Electron (No FS Bridge)"
                      : "❌ Web Browser (No FS)"}
              </p>
              <p className="text-blue-800 dark:text-blue-200">
                <strong>User Agent:</strong>{" "}
                {navigator.userAgent.includes("Electron") ? "Electron Detected" : "Standard Browser"}
              </p>
            </div>
          </div>

          {/* Notification Configuration */}
          <div className="bg-foregroundx /50 border border-border dark:border-gray-700 rounded-lg p-4">
            <h2 className="text-lg font-semibold  dark:text-white mb-4">📝 Notification Configuration</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                  Title
                </label>
                <input
                  id="title"
                  type="text"
                  value={notificationTitle}
                  onChange={(e) => setNotificationTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500  dark:text-white"
                  placeholder="Enter notification title"
                />
              </div>

              <div>
                <label htmlFor="body" className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                  Message Body
                </label>
                <textarea
                  id="body"
                  value={notificationBody}
                  onChange={(e) => setNotificationBody(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500  dark:text-white"
                  placeholder="Enter notification message"
                />
              </div>

              <div>
                <label htmlFor="sound" className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                  Custom Sound (Desktop Only)
                </label>
                <input
                  id="sound"
                  type="text"
                  value={customSound}
                  onChange={(e) => setCustomSound(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500  dark:text-white"
                  placeholder="./notification.mp3 or URL to sound file"
                  disabled={!isDesktopApp()}
                />
                {!isDesktopApp() && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Custom sounds are only available in the desktop app
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Notification Buttons */}
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-4">🔔 Test Notifications</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={sendBasicNotification}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                📢 Send Smart Notification
              </button>

              <button
                onClick={sendToDesktopNotification}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!isDesktopApp()}
              >
                🖥️ ToDesktop API Only
              </button>

              <button
                onClick={sendNotificationWithSound}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!isDesktopApp() && !customSound}
              >
                🔊 Send with Sound
              </button>

              <button
                onClick={sendTimedNotification}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-colors"
              >
                ⏰ Send Delayed (3s)
              </button>
            </div>
          </div>

          {/* Status Display */}
          {notificationStatus && (
            <div className="bg-foregroundx /50 border border-border dark:border-gray-700 rounded-lg p-4">
              <h3 className="text-sm font-medium  dark:text-white mb-2">📊 Status</h3>
              <p className="text-sm text-primary dark:text-gray-300 font-mono">{notificationStatus}</p>
            </div>
          )}

          {/* Usage Examples */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-yellow-900 dark:text-yellow-100 mb-4">💡 Usage Examples</h2>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-yellow-800 dark:text-yellow-200 font-medium">
                  ToDesktop Notification API (Recommended):
                </p>
                <code className="block mt-1 p-2 bg-yellow-100 dark:bg-yellow-900/40 rounded text-xs text-yellow-900 dark:text-yellow-100">
                  import {"{"} notification {"}"} from &apos;@todesktop/client-core&apos;;
                  <br />
                  await notification.create({"{"} title: &apos;Hello&apos;, body: &apos;Message&apos; {"}"});
                </code>
              </div>

              <div>
                <p className="text-yellow-800 dark:text-yellow-200 font-medium">Platform Detection:</p>
                <code className="block mt-1 p-2 bg-yellow-100 dark:bg-yellow-900/40 rounded text-xs text-yellow-900 dark:text-yellow-100">
                  import {"{"} platform {"}"} from &apos;@todesktop/client-core&apos;;
                  <br />
                  platform.todesktop.isDesktopApp()
                </code>
              </div>

              <div>
                <p className="text-yellow-800 dark:text-yellow-200 font-medium">HTML5 Notification (Fallback):</p>
                <code className="block mt-1 p-2 bg-yellow-100 dark:bg-yellow-900/40 rounded text-xs text-yellow-900 dark:text-yellow-100">
                  new Notification(&apos;Title&apos;, {"{"} body: &apos;Message&apos; {"}"});
                </code>
              </div>

              <div>
                <p className="text-yellow-800 dark:text-yellow-200 font-medium">With Custom Sound (Desktop):</p>
                <code className="block mt-1 p-2 bg-yellow-100 dark:bg-yellow-900/40 rounded text-xs text-yellow-900 dark:text-yellow-100">
                  new Notification(&apos;Title&apos;, {"{"} body: &apos;Message&apos;, sound: &apos;./sound.mp3&apos;{" "}
                  {"}"});
                </code>
              </div>
            </div>
          </div>

          {/* File System Section */}
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-4">📁 File System Access</h2>

            <div className="space-y-4">
              {/* Read Config Button */}
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={readClaudeConfig}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!isDesktopApp() && !hasNativeFileAccess()}
                >
                  📖 Read Claude Config
                </button>

                {selectedProject && (
                  <button
                    onClick={clearSelection}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                  >
                    🗂️ Clear Selection
                  </button>
                )}
              </div>

              {/* Status */}
              {fileSystemStatus && (
                <div className="bg-purple-100 dark:bg-purple-900/40 border border-purple-200 dark:border-purple-700 rounded p-3">
                  <p className="text-sm text-purple-800 dark:text-purple-200 font-mono">{fileSystemStatus}</p>
                </div>
              )}

              {/* Projects List */}
              {claudeProjects.length > 0 && (
                <div className="bg-background  border border-purple-200 dark:border-purple-700 rounded-lg p-4">
                  <h3 className="text-md font-semibold text-purple-900 dark:text-purple-100 mb-3">
                    🏗️ Claude Projects ({claudeProjects.length})
                  </h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {claudeProjects.map((project, index) => (
                      <div
                        key={index}
                        onClick={() => selectProject(project)}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors hover:bg-purple-50 dark:hover:bg-purple-900/30 ${
                          selectedProject?.path === project.path
                            ? "border-purple-500 bg-purple-50 dark:bg-purple-900/30"
                            : "border-border dark:border-gray-600"
                        }`}
                      >
                        <p className="text-sm font-medium  dark:text-white truncate">
                          {project.path.split("/").pop() || project.path}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{project.path}</p>
                        {project.mcpServers && Object.keys(project.mcpServers).length > 0 && (
                          <p className="text-xs text-purple-600 dark:text-purple-300 mt-1">
                            🔗 {Object.keys(project.mcpServers).length} MCP server(s)
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Selected Project Details */}
              {selectedProject && (
                <div className="bg-background  border border-purple-200 dark:border-purple-700 rounded-lg p-4">
                  <h3 className="text-md font-semibold text-purple-900 dark:text-purple-100 mb-3">
                    📋 Project Details
                  </h3>

                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-primary dark:text-gray-300">Project Path:</p>
                      <p className="text-sm  dark:text-gray-400 font-mono bg-foregroundx dark:bg-gray-700 p-2 rounded">
                        {selectedProject.path}
                      </p>
                    </div>

                    {selectedProject.mcpServers && Object.keys(selectedProject.mcpServers).length > 0 ? (
                      <div>
                        <p className="text-sm font-medium text-primary dark:text-gray-300 mb-2">
                          MCP Servers ({Object.keys(selectedProject.mcpServers).length}):
                        </p>
                        <div className="space-y-2">
                          {Object.entries(selectedProject.mcpServers).map(([serverName, serverConfig]) => (
                            <div key={serverName} className="bg-foregroundx dark:bg-gray-700 p-3 rounded">
                              <p className="text-sm font-medium  dark:text-white">🔗 {serverName}</p>
                              <pre className="text-xs  dark:text-gray-400 mt-1 overflow-x-auto">
                                {JSON.stringify(serverConfig, null, 2)}
                              </pre>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          No MCP servers configured for this project.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Info about File System Access */}
              {!isDesktopApp() && (
                <div className="bg-yellow-100 dark:bg-yellow-900/40 border border-yellow-200 dark:border-yellow-700 rounded p-3">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    💡 File system access is only available in desktop apps (Electron/ToDesktop).
                  </p>
                </div>
              )}

              {isLocalElectron() && claudeProjects.length === 0 && !fileSystemStatus && (
                <div className="bg-blue-100 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-700 rounded p-3">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    🔧 Local Electron detected - Click &quot;Read Claude Config&quot; to load mock Claude projects data
                    for testing.
                  </p>
                </div>
              )}

              {isToDesktopApp() && claudeProjects.length === 0 && !fileSystemStatus && (
                <div className="bg-blue-100 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-700 rounded p-3">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    📖 Click &quot;Read Claude Config&quot; to load your Claude projects from ~/.claude.json
                    <br />💡 Make sure the File System plugin is enabled in ToDesktop Builder with readFile permission.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
